import logger from 'helpers/logger';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import React, {
    forwardRef,
    memo,
    Suspense,
    useImperativeHandle,
    useRef,
    useCallback,
    useEffect
} from 'react';
import { LoadingIndicator } from 'sharedComponents';
import { CHART_PLUGINS } from './constants';
import { apiCallStatus } from 'helpers/constants';

const LazyChartComponent = React.lazy(async () => {
    const { default: Highcharts } = await import(/* webpackChunkName: "highCharts" */ 'highcharts');
    const { default: HighchartsReact } = await import(/* webpackChunkName: "highcharts-react-official" */ 'highcharts-react-official');
    const { default: Highcharts3D } = await import(/* webpackChunkName: "highcharts-3d" */ 'highcharts/highcharts-3d');
    Highcharts3D(Highcharts);

    // We must allow this reference in order to have dynamic, locally created svg files on the charts
    Highcharts.AST.allowedReferences.push('data:');

    // This is a patch to keep having own SVG's embedded in the highcharts
    // if an svg does not work, please check whether all it's tags and attributes are supported or not
    Highcharts.AST.allowedAttributes.push('viewBox', 'xmlns', 'stroke-linejoin', 'x2');
    Highcharts.AST.allowedTags.push('line');
    Highcharts.seriesTypes.line.prototype.drawLegendSymbol = Highcharts.seriesTypes.column.prototype.drawLegendSymbol;

    const pluginImports = {
        [CHART_PLUGINS.NO_DATA_TO_DISPLAY]: () => import(/* webpackChunkName: "highCharts" */ 'highcharts/modules/no-data-to-display'),
        [CHART_PLUGINS.BOOST]: () => import(/* webpackChunkName: "highCharts" */ 'highcharts/modules/boost'),
        [CHART_PLUGINS.EXPORTING]: () => import(/* webpackChunkName: "highCharts" */ 'highcharts/modules/exporting'),
        [CHART_PLUGINS.STOCK]: () => import(/* webpackChunkName: "highCharts" */ 'highcharts/modules/stock'),
        [CHART_PLUGINS.OFFLINE_EXPORTING]: () => import(/* webpackChunkName: "highCharts" */ 'highcharts/modules/offline-exporting')
    };

    const InternalChart = (props, ref) => {
        const containerRef = useRef(null);
        const chartRef = useRef(null);
        const creatingChartRef = useRef(false);

        const loadModules = useCallback(async () => {
            if (!props.modules) { return; }

            for (const module of props.modules) {
                const importPlugin = pluginImports[module];
                try {
                    if (importPlugin) {
                        const importedPlugin = await importPlugin();
                        if (importedPlugin?.default) {
                            importedPlugin.default(Highcharts);
                        } else {
                            logger.debug('Error loading highchart plugin:', module);
                        }
                    } else {
                        logger.debug('Unknown highchart plugin:', module);
                    }
                } catch (error) {
                    logger.debug('Exception loading highchart plugin:', module, error);
                }
            }
        }, [props.modules]);

        const createUpdateChart = useCallback(debounce((debProps) => {
            if (!chartRef.current && !creatingChartRef.current) {
                creatingChartRef.current = true;
                loadModules().then(() => {
                    logger.debug('Created chart', debProps.container);
                    creatingChartRef.current = false;
                });
            } else {
                if (chartRef?.current) {
                    logger.debug('Update chart', debProps.container);
                    debProps.setChartLoader(apiCallStatus.SUCCESS);
                }
                else {
                    logger.warn('Update skipped, invalid highchart instance!');
                }
            }
        }, 100), []);

        useEffect(() => {
            return () => {
                createUpdateChart.cancel(); // cancel delayed execution
                // Destroy chart only if unmounting.
                if (chartRef.current) {
                    if (props.destroy) {
                        props.destroy(null);
                    }
                    chartRef.current = null;
                    logger.debug('Chart destroyed', props.container);
                }
            };
        }, []);


        useEffect(() => {
            createUpdateChart(props);
        });

        useImperativeHandle(
            ref,
            () => ({
                get chart() {
                    return chartRef.current;
                },
                container: containerRef
            }),
            []
        );

        // Create container for the chart
        return props?.options?.series?.length > 0 && <HighchartsReact
            highcharts={Highcharts}
            options={props.options}
            ref={chartRef}
            callback={props.callback}
        />;
    };
    InternalChart.displayName = 'InternalChart3D';

    InternalChart.propTypes = {
        modules: PropTypes.arrayOf(PropTypes.string),
        options: PropTypes.object,
        callback: PropTypes.func,
        destroy: PropTypes.func,
        container: PropTypes.string,
        setChartLoader: PropTypes.func
    };

    return {
        default: memo(forwardRef(InternalChart))
    };
});

const Chart3D = forwardRef((props, ref) => {
    return <Suspense fallback={<LoadingIndicator />}>
        <LazyChartComponent {...props} ref={ref} />
    </Suspense>;
});

Chart3D.displayName = 'LazyChartWrapper3D';

export default Chart3D;
